/**
 * Game Night hub card for the TV scoreboard (/scoreboard/:pin).
 *
 * The host turns the scoreboard on for one night (off by default), picks a
 * top-N limit, and gets the URL and a QR code to open it on a second TV.
 * The board shows standings through the last completed round, the same as
 * the projector standings slide. Each change saves
 * at once through PUT /api/events/:id/scoreboard. The server then pushes
 * the new board to any open scoreboard.
 */
import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import api from '../../utils/api';

export interface ScoreboardSettings {
  scoreboardEnabled: boolean;
  scoreboardTopN: number | null;
}

interface Props {
  eventId: string;
  pin: string;
  settings: ScoreboardSettings;
  // False when the org plan lacks the tvScoreboard feature.
  available: boolean;
  onSaved: (next: ScoreboardSettings) => void;
}

const TOP_N_OPTIONS: Array<{ value: number | null; label: string }> = [
  { value: null, label: 'All teams' },
  { value: 3, label: 'Top 3' },
  { value: 5, label: 'Top 5' },
  { value: 10, label: 'Top 10' },
];

export default function ScoreboardCard({ eventId, pin, settings, available, onSaved }: Props) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const fullUrl = `${window.location.origin}/scoreboard/${pin}`;
  const shortUrl = `${window.location.host}/scoreboard/${pin}`;
  const on = settings.scoreboardEnabled;

  const save = async (body: Record<string, unknown>) => {
    setSaving(true);
    setError(null);
    try {
      const res = await api.put(`/api/events/${eventId}/scoreboard`, body);
      const s = res.data.data.scoreboard;
      onSaved({
        scoreboardEnabled: s.scoreboardEnabled,
        scoreboardTopN: s.scoreboardTopN,
      });
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      setError(msg || 'Failed to save the scoreboard settings');
    } finally {
      setSaving(false);
    }
  };

  const copy = () => {
    navigator.clipboard?.writeText(fullUrl).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => {},
    );
  };

  return (
    <div className="gh-card">
      <span className="gh-overline">TV SCOREBOARD</span>
      <span className="gh-card-body">
        Standings for a second TV. It updates when each round completes, at the same time as the standings slide.
      </span>

      {!available ? (
        <span className="gh-card-body gh-sb-upsell">Your plan does not include the TV scoreboard.</span>
      ) : (
        <>
          <label className="gh-sb-toggle">
            <input
              type="checkbox"
              checked={on}
              disabled={saving}
              onChange={(e) => save({ enabled: e.target.checked })}
            />
            <span>Show the scoreboard tonight</span>
          </label>

          <label className="gh-sb-field">
            <span className="gh-sb-label">Show</span>
            <select
              className="gh-sb-select"
              value={settings.scoreboardTopN ?? ''}
              disabled={saving}
              onChange={(e) => save({ topN: e.target.value === '' ? null : Number(e.target.value) })}
            >
              {TOP_N_OPTIONS.map((o) => (
                <option key={o.label} value={o.value ?? ''}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          {on && (
            <div className="gh-sb-share">
              <div className="gh-sb-qr" aria-label="QR code for the scoreboard address">
                <QRCodeSVG value={fullUrl} size={88} bgColor="#ffffff" fgColor="#000000" includeMargin />
              </div>
              <div className="gh-sb-share-main">
                <div className="gh-url-row">
                  <span className="gh-url">{shortUrl}</span>
                  <button type="button" className="gh-url-copy" onClick={copy}>
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <a className="gh-pill-action" href={`/scoreboard/${pin}`} target="_blank" rel="noopener noreferrer">
                  Open scoreboard
                </a>
              </div>
            </div>
          )}

          {error && <span className="gh-card-note gh-sb-error">{error}</span>}
        </>
      )}
    </div>
  );
}
